import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flame, Award } from "lucide-react";
import { api } from "@/lib/api";
import { Button, Avatar, StarPicker, Field, Loading } from "@/components/brand";

export default function Complete() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  const b = useQuery({ queryKey: ["booking", id], queryFn: () => api(`/bookings/${id}`) });
  const stats = useQuery({ queryKey: ["me", "stats"], queryFn: () => api("/me/stats") });

  const review = useMutation({
    mutationFn: () => api("/reviews", { method: "POST", body: { booking_id: id, rating, knowledge: rating, communication: rating, energy: rating, professionalism: rating, comment } }),
    onSuccess: () => { setDone(true); qc.invalidateQueries({ queryKey: ["booking", id] }); toast.success("Thanks for your feedback!"); },
    onError: (e) => toast.error(e.message),
  });

  if (b.isLoading) return <Loading />;
  const d = b.data || {};

  return (
    <div data-testid="complete-screen" className="max-w-lg mx-auto text-center">
      <div className="font-accent text-4xl text-brand -rotate-3 leading-tight mt-4">Stronger Healthier<br />Happier Together</div>
      <h1 className="font-display text-4xl text-ink tracking-tight mt-4">Session Complete!</h1>
      <p className="font-body text-mutedgrey mt-1">Great work. You showed up and put in the effort.</p>

      <div className="flex justify-center gap-4 mt-6">
        <div className="flex-1 rounded-xl2 bg-brand-soft p-4">
          <Flame className="text-brand mx-auto" size={24} />
          <div className="font-display text-3xl text-brand mt-1">{stats.data?.streak ?? "–"}</div>
          <div className="font-body text-xs text-ink">day streak</div>
        </div>
        <div className="flex-1 rounded-xl2 bg-brand-soft p-4">
          <Award className="text-brand mx-auto" size={24} />
          <div className="font-display text-3xl text-brand mt-1">{stats.data?.total_workouts ?? "–"}</div>
          <div className="font-body text-xs text-ink">total workouts</div>
        </div>
      </div>

      {!done && !d.reviewed ? (
        <div className="mt-8 text-left">
          <div className="flex items-center gap-2 justify-center">
            <Avatar uri={d.trainer_avatar} size={44} name={d.trainer_name} />
            <div className="text-center"><div className="font-display text-lg text-ink">Rate {d.trainer_name}</div><div className="font-body text-xs text-mutedgrey">How was your session?</div></div>
          </div>
          <div className="flex justify-center mt-4"><StarPicker value={rating} onChange={setRating} testID="review-stars" /></div>
          <div className="mt-4"><Field as="textarea" testID="review-comment" placeholder="Share a few words (optional)" value={comment} onChange={(e) => setComment(e.target.value)} /></div>
          <Button testID="review-submit" title="Submit Review" loading={review.isPending} onClick={() => review.mutate()} full className="mt-4" />
        </div>
      ) : (
        <p className="font-body text-success font-semibold mt-6">Review submitted — thank you!</p>
      )}

      <Button testID="complete-home-button" title="Back to Home" variant="dark" onClick={() => navigate("/discover")} full className="mt-6" />
    </div>
  );
}
